import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Loader2, Check, X } from 'lucide-react';
import { resetPassword, getResetTokenInfo, setPasswordWithToken } from '../../api/auth';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { passwordRequirements } from '../../utils/passwordRequirements';
import { humanizeAuthError } from '../../utils/authErrors';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<{ valid: boolean; hasPassword?: boolean; email?: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
      setTokenInfo({ valid: false });
      return;
    }
    getResetTokenInfo(token)
      .then(setTokenInfo)
      .catch(() => setTokenInfo({ valid: false }));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    if (!password.trim()) {
      setError('Please enter a new password');
      return;
    }

    // Validate password requirements
    const failedRequirements = passwordRequirements.filter((req) => !req.test(password));
    if (failedRequirements.length > 0) {
      setError('Password does not meet requirements');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      if (tokenInfo?.hasPassword === false) {
        await setPasswordWithToken(token, password);
      } else {
        await resetPassword(token, password);
      }
      setIsSuccess(true);
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: unknown) {
      setError(humanizeAuthError(err, 'Failed to reset password. The link may have expired.'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!tokenInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center px-4 pt-safe">
        <div className="w-full max-w-md">
          <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-lg shadow-lg border border-border-subtle-light dark:border-border-subtle-dark p-8">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-950/30 rounded-full flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                Password Reset Successful
              </h1>
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                Your password has been reset. Redirecting to sign in...
              </p>
              <div className="flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary-700 dark:text-primary-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center px-4 pt-safe">
      <div className="w-full max-w-md">
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-lg shadow-lg border border-border-subtle-light dark:border-border-subtle-dark p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              {tokenInfo.hasPassword === true ? 'Reset your password' : 'Set your password'}
            </h1>
            <p className="text-text-secondary-light dark:text-text-secondary-dark">Enter your new password</p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1"
              >
                New Password
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-border-hover-light dark:border-border-hover-dark rounded-md bg-surface-raised-light dark:bg-surface-base-dark text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isLoading || !token}
              />

              {/* Always-visible requirements — same pattern as RegisterPage so
                  users know what's expected before they type the wrong thing. */}
              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req) => {
                  const passes = password.length > 0 && req.test(password);
                  const idle = password.length === 0;
                  return (
                    <div key={req.label} className="flex items-center gap-2 text-xs">
                      {passes ? (
                        <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                      ) : (
                        <X className={`w-3 h-3 ${idle ? 'text-text-tertiary-light dark:text-text-tertiary-dark' : 'text-text-secondary-light dark:text-text-secondary-dark'}`} />
                      )}
                      <span
                        className={
                          passes
                            ? 'text-green-600 dark:text-green-400'
                            : idle
                              ? 'text-text-tertiary-light dark:text-text-tertiary-dark'
                              : 'text-text-secondary-light dark:text-text-secondary-dark'
                        }
                      >
                        {req.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !token}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-700 hover:bg-primary-800 dark:glow-primary dark:hover:glow-primary-hover text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Reset Password
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
